import { useEffect, useState } from 'react';

const ANIMATION_TIMEOUT_MS = 2000;
const QUERY_PARAM_NAME = 'waitForContentReady';

export const shouldEnableContentReadyTracking = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  return params.has(QUERY_PARAM_NAME);
};

export const useContentReady = (enabled: boolean): boolean => {
  const [isReady, setIsReady] = useState(() => {
    return !enabled;
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;

    const checkReadiness = async () => {
      if (cancelled) return;

      try {
        // Wait for document ready first to ensure all elements are in DOM
        await checkDocumentReady();

        if (cancelled) return;

        // Now check images, fonts, and animations
        await Promise.all([
          checkImagesLoaded(),
          checkFontsLoaded(),
          checkAnimationsComplete()
        ]);

        if (!cancelled) {
          setIsReady(true);
        }
      } catch (error) {
        console.error('Error checking content readiness:', error);
        if (!cancelled) {
          setIsReady(true);
        }
      }
    };

    checkReadiness();

    return () => {
      cancelled = true;
    };
  }, []);

  return isReady;
};

const checkDocumentReady = (): Promise<void> => {
  return new Promise(resolve => {
    if (document.readyState === 'complete') {
      resolve();
      return;
    }

    const handleLoad = () => {
      window.removeEventListener('DOMContentLoaded', handleLoad);
      window.removeEventListener('load', handleLoad);
      resolve();
    };

    window.addEventListener('DOMContentLoaded', handleLoad);
    window.addEventListener('load', handleLoad);
  });
};

const checkImagesLoaded = (): Promise<void> => {
  return new Promise(resolve => {
    const images = Array.from(document.querySelectorAll('img'));
    const backgroundImageUrls = getBackgroundImageUrls();

    const totalChecks = images.length + backgroundImageUrls.length;

    if (totalChecks === 0) {
      resolve();
      return;
    }

    let loadedCount = 0;

    const handleLoad = () => {
      loadedCount++;
      if (loadedCount === totalChecks) {
        resolve();
      }
    };

    // Check <img> tags
    const incompleteImages = images.filter(img => !img.complete);
    if (incompleteImages.length === 0 && backgroundImageUrls.length === 0) {
      resolve();
      return;
    }

    // Already loaded images
    loadedCount += images.length - incompleteImages.length;

    // Attach listeners to incomplete <img> tags
    incompleteImages.forEach(img => {
      img.addEventListener('load', handleLoad, { once: true });
      img.addEventListener('error', handleLoad, { once: true });
    });

    // Check CSS background images
    backgroundImageUrls.forEach(url => {
      const img = new Image();
      img.onload = handleLoad;
      img.onerror = handleLoad;
      img.src = url;
    });

    // If all images were already loaded
    if (loadedCount === totalChecks) {
      resolve();
    }
  });
};

const getBackgroundImageUrls = (): string[] => {
  const urls: string[] = [];
  const elements = document.querySelectorAll('*');

  elements.forEach(element => {
    const style = window.getComputedStyle(element);
    const bgImage = style.backgroundImage;

    if (bgImage && bgImage !== 'none') {
      // Extract URL from background-image: url("...")
      const matches = bgImage.match(/url\(['"]?([^'"]+)['"]?\)/g);
      if (matches) {
        matches.forEach(match => {
          const url = match.replace(/url\(['"]?/, '').replace(/['"]?\)/, '');
          if (url && !url.startsWith('data:')) {
            urls.push(url);
          }
        });
      }
    }
  });

  return [...new Set(urls)]; // Remove duplicates
};

const checkFontsLoaded = (): Promise<void> => {
  if (!document.fonts || !document.fonts.ready) {
    return Promise.resolve();
  }

  return document.fonts.ready.then(() => undefined);
};

const checkAnimationsComplete = (): Promise<void> => {
  return new Promise(resolve => {
    if (!document.getAnimations) {
      resolve();
      return;
    }

    const animations = document.getAnimations();

    if (animations.length === 0) {
      resolve();
      return;
    }

    const animationPromises = animations.map(animation =>
      animation.finished.catch(() => undefined)
    );

    const timeoutPromise = new Promise<void>(timeoutResolve => {
      setTimeout(timeoutResolve, ANIMATION_TIMEOUT_MS);
    });

    Promise.race([Promise.all(animationPromises), timeoutPromise]).then(() => {
      resolve();
    });
  });
};
